import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Palette, Upload, Save, Check, Loader2, Trash2, Sparkles, TrendingUp } from "lucide-react";
import { Img } from "@/components/Img";
import EmailNotificationsCard from "@/components/EmailNotificationsCard";
import OfferNotificationsCard from "@/components/OfferNotificationsCard";
import SignupNotificationsCard from "@/components/SignupNotificationsCard";
import TeamChatWebhooksCard from "@/components/TeamChatWebhooksCard";
import ApiKeysCard from "@/components/ApiKeysCard";
import DocuSignCard from "@/components/DocuSignCard";
import TwilioCard from "@/components/TwilioCard";
import TwilioComplianceCard from "@/components/TwilioComplianceCard";
import TcpaComplianceCard from "@/components/TcpaComplianceCard";
import NumberBrandMappingCard from "@/components/NumberBrandMappingCard";
import BookingLinkCard from "@/components/BookingLinkCard";
import ManagedNumbersCard from "@/components/ManagedNumbersCard";
import TollFreeNumbersCard from "@/components/TollFreeNumbersCard";
import VoiceCard from "@/components/VoiceCard";
import ApexCard from "@/components/ApexCard";
import ProfileCard from "@/components/ProfileCard";

const PRESETS = ["#10B981", "#0EA5E9", "#6366F1", "#A855F7", "#EC4899", "#F97316", "#EAB308", "#0B132B"];
const MAX_LOGO_BYTES = 4 * 1024 * 1024; // 4MB raw — downscaled on client before upload
const MAX_LOGO_DIMENSION = 800;         // downscale to 800px on the long side
const ACCEPT = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];

export default function Settings() {
  const { user, refresh } = useAuth();
  const [logo, setLogo] = useState("");
  const [accent, setAccent] = useState("#10B981");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/me/brand").then((r) => {
      if (!alive) return;
      setLogo(r.data.brand_logo || "");
      setAccent(r.data.brand_accent || "#10B981");
      setCompany(r.data.brand_company || "");
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const onPickLogo = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPT.includes(f.type)) {
      setErr("Please upload a PNG, JPG, WebP, or SVG.");
      return;
    }
    if (f.size > MAX_LOGO_BYTES) {
      setErr("Logo file is too large (max 4MB).");
      return;
    }
    // SVG: pass through unchanged — it's already vector.
    if (f.type === "image/svg+xml") {
      const reader = new FileReader();
      reader.onload = () => setLogo(String(reader.result || ""));
      reader.onerror = () => setErr("Could not read that file.");
      reader.readAsDataURL(f);
      return;
    }
    // Raster: auto-downscale to MAX_LOGO_DIMENSION px on the long side.
    // Prefer PNG for sources with transparency (preserves alpha channel), but
    // fall back to JPEG when the PNG data URL exceeds the backend size cap
    // (~800K chars). Canvas re-encoding strips palette optimization, so a
    // pre-compressed 330KB PNG can balloon to 1.5MB after round-trip. This
    // auto-fallback eliminates the "compress it first" friction entirely.
    const MAX_DATA_URL_CHARS = 720_000;   // leaves headroom under backend 800K limit
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = Math.min(1, MAX_LOGO_DIMENSION / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);

      let dataUrl;
      if (f.type === "image/png") {
        // Try PNG first — preserves transparency for dark-mode surfaces.
        dataUrl = canvas.toDataURL("image/png");
        // If bloated by canvas re-encoding, fall back to JPEG at high quality.
        if (dataUrl.length > MAX_DATA_URL_CHARS) {
          dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        }
        // If STILL too big (rare — huge logos), ratchet quality down.
        if (dataUrl.length > MAX_DATA_URL_CHARS) {
          dataUrl = canvas.toDataURL("image/jpeg", 0.75);
        }
      } else {
        dataUrl = canvas.toDataURL("image/jpeg", 0.88);
      }
      setLogo(dataUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setErr("Could not read that image.");
    };
    img.src = url;
  };

  const clearLogo = () => {
    setLogo("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSave = async () => {
    setBusy(true);
    setErr("");
    setStatus("");
    try {
      await api.put("/me/brand", {
        brand_logo: logo,
        brand_accent: accent,
        brand_company: company,
      });
      await refresh();
      setStatus("saved");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="settings-page">
      <div className="mb-8">
        <div className="label-eyebrow">Settings</div>
        <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight mt-1">Branded Share Cards</h1>
        <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
          Your logo and accent color will appear on every shareable image you generate from a Story Arc — and on the footer of your Property Analysis Reports.
        </p>
      </div>

      {/* Newspaper-style balanced columns. Unlike grid, CSS `columns` lets the
          browser measure each card and pack them so the LEFT and RIGHT columns
          end up the same height — no more ragged gaps at the bottom of the
          shorter column. Reading flow: top-to-bottom of column 1, then
          top-to-bottom of column 2. `break-inside-avoid` keeps each card as
          one atomic unit (never split mid-card between columns). */}
      <div
        className="columns-1 md:columns-2 gap-5 [&>*]:break-inside-avoid [&>*]:mb-5"
        data-testid="settings-cards-grid"
      >
        {/* Row 1 — Profile paired visually with the Live Preview */}
        <ProfileCard />

        {/* Live preview card */}
        <div className="card" data-testid="settings-preview-card">
          <div className="label-eyebrow mb-3">Live preview</div>
          <BrandPreview logo={logo} accent={accent} company={company || user?.name || "iSpeedToClose"} />
          <p className="text-xs text-[var(--ink-3)] mt-3 italic">
            This is a miniature of the top of your share cards.
          </p>
        </div>

        {/* Logo upload */}
        <div className="card" data-testid="settings-logo-card">
          <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Upload size={11} /> Your logo</div>
          <div className="flex items-center gap-5">
            <div
              className="w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center shrink-0 overflow-hidden bg-white"
              style={{ borderColor: "var(--line)" }}
            >
              {logo ? (
                <Img src={logo} alt="Brand logo preview" className="max-w-full max-h-full object-contain" data-testid="settings-logo-preview" />
              ) : (
                <Building2 size={28} className="text-[var(--ink-3)]" />
              )}
            </div>
            <div className="flex-1">
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT.join(",")}
                onChange={onPickLogo}
                className="hidden"
                id="logo-upload"
                data-testid="settings-logo-input"
              />
              <div className="flex gap-2 flex-wrap">
                <label htmlFor="logo-upload" className="btn-outline cursor-pointer" data-testid="settings-logo-upload-button">
                  <Upload size={14} /> {logo ? "Replace logo" : "Upload logo"}
                </label>
                {logo && (
                  <button type="button" className="btn-outline text-[var(--warn)]" onClick={clearLogo} data-testid="settings-logo-clear-button">
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-[var(--ink-3)] mt-2">
                PNG, JPG, WebP, or SVG · max 4MB · auto-downscaled.
              </p>
            </div>
          </div>
        </div>

        {/* Company name */}
        <div className="card" data-testid="settings-company-card">
          <label className="label-eyebrow block mb-2" htmlFor="brand-company">Company name (optional)</label>
          <input
            id="brand-company"
            type="text"
            className="form-input"
            placeholder="e.g. Atlas Property Group"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            maxLength={120}
            data-testid="settings-company-input"
          />
          <p className="text-xs text-[var(--ink-3)] mt-2">
            Shown in the footer of share cards. Leave blank to use your account name.
          </p>
        </div>

        {/* Accent color — swatches wrap gracefully at column-width. */}
        <div className="card" data-testid="settings-accent-card">
          <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Palette size={11} /> Accent color</div>
          <div className="flex items-center gap-3 flex-wrap">
            {PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setAccent(c)}
                className="w-10 h-10 rounded-lg border-2 transition-transform hover:scale-110"
                style={{
                  background: c,
                  borderColor: accent.toLowerCase() === c.toLowerCase() ? "var(--ink)" : "transparent",
                  boxShadow: accent.toLowerCase() === c.toLowerCase() ? "0 0 0 2px #fff inset" : "none",
                }}
                aria-label={`Choose ${c}`}
                data-testid={`settings-accent-preset-${c.replace("#", "").toLowerCase()}`}
              />
            ))}
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                className="w-10 h-10 rounded-lg border-0 cursor-pointer"
                data-testid="settings-accent-picker"
                aria-label="Custom accent color"
              />
              <input
                type="text"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                className="form-input font-mono uppercase tracking-wider"
                style={{ width: 100 }}
                maxLength={7}
                data-testid="settings-accent-hex"
              />
            </div>
          </div>
          <p className="text-xs text-[var(--ink-3)] mt-3">
            Used for highlights, chips, and the verdict badge.
          </p>
        </div>

        {/* Notifications & integrations. Order tuned so short cards sit near
            tall cards — helps the browser balance column heights better. */}
        <EmailNotificationsCard />
        <OfferNotificationsCard />
        <SignupNotificationsCard />
        <TeamChatWebhooksCard />
        <DocuSignCard />
        <TwilioCard />
        <TwilioComplianceCard />
        <TcpaComplianceCard />
        <BookingLinkCard />
        <ManagedNumbersCard />
        <NumberBrandMappingCard />
        <TollFreeNumbersCard />
        <VoiceCard />
        <ApexCard />
        <ApiKeysCard />
      </div>

      {err && <div className="banner-err mt-5" data-testid="settings-error">{err}</div>}

      <div className="flex items-center gap-3 mt-6 pt-6 border-t border-[var(--line)]">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          className="btn-money"
          data-testid="settings-save-button"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : status === "saved" ? <Check size={14} /> : <Save size={14} />}
          {busy ? "Saving…" : status === "saved" ? "Saved!" : "Save brand settings"}
        </button>
        <span className="text-xs text-[var(--ink-3)]">Logged in as <strong>{user?.email}</strong></span>
      </div>
    </div>
  );
}

function BrandPreview({ logo, accent, company }) {
  return (
    <div
      className="rounded-xl overflow-hidden shadow-lg"
      style={{ background: "linear-gradient(135deg, #0B132B 0%, #1B2440 60%, #06321F 100%)", aspectRatio: "1 / 1" }}
      data-testid="settings-preview"
    >
      <div className="p-5 h-full flex flex-col text-white relative">
        <div
          className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30"
          style={{ background: `radial-gradient(circle, ${accent}88 0%, transparent 70%)` }}
        />
        <div className="flex items-center gap-3 relative">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden shrink-0"
            style={{ background: logo ? "#fff" : accent }}
          >
            {logo ? (
              <Img src={logo} alt="" className="max-w-full max-h-full object-contain" />
            ) : (
              <Building2 size={22} color="#fff" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-none truncate">{company}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/55 mt-1.5">Deal Story Arc</div>
          </div>
          <div
            className="ml-auto px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest border flex items-center gap-1.5"
            style={{ background: accent + "22", color: accent, borderColor: accent + "77" }}
          >
            <Sparkles size={10} /> GO
          </div>
        </div>
        <div className="mt-auto">
          <div className="text-white/55 text-[10px] uppercase tracking-widest">Projected profit</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-white/60 font-bold tabular-nums">$28K</span>
            <span className="text-white/40">→</span>
            <span className="font-display font-black text-2xl tabular-nums" style={{ color: accent }}>$42K</span>
          </div>
          <div className="text-xs font-semibold flex items-center gap-1 mt-1" style={{ color: accent }}>
            <TrendingUp size={12} /> +$14K
          </div>
        </div>
      </div>
    </div>
  );
}
